import React, { useState } from 'react';
import { CheckCircle, Clock, Percent, Shield, ChevronDown, ChevronUp } from 'lucide-react';
import i18n from '../../i18n/config';
import CTASection from './CTASection';

interface LoanType {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  color: string;
  period: string;
  rate: string;
  security: string;
  benefits: string[];
  details: string[];
  faq: Array<{
    question: string;
    answer: string;
  }>;
}

interface LoanTypeContentProps {
  loanType: LoanType;
}

const LoanTypeContent: React.FC<LoanTypeContentProps> = ({ loanType }) => {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const Icon = loanType.icon;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="text-center mb-16">
        <div className="flex justify-center mb-6">
          <div 
            className="p-6 rounded-full shadow-lg"
            style={{ backgroundColor: `${loanType.color}20` }}
          >
            <Icon 
              className="h-16 w-16" 
              style={{ color: loanType.color }}
            />
          </div>
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-[#2c3e50] mb-4">
          {loanType.title}
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
          {loanType.description}
        </p>
      </div>

      {/* Key Information */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
        <div className="bg-white p-8 rounded-lg shadow-lg border-l-4" style={{ borderLeftColor: loanType.color }}>
          <div className="flex items-center mb-4">
            <Clock className="h-8 w-8 mr-3" style={{ color: loanType.color }} />
            <h3 className="text-xl font-semibold text-[#2c3e50]">
              {i18n.t('loan_types_page.info.period')}
            </h3>
          </div>
          <p className="text-lg text-gray-700 font-medium">{loanType.period}</p>
        </div>

        <div className="bg-white p-8 rounded-lg shadow-lg border-l-4" style={{ borderLeftColor: loanType.color }}>
          <div className="flex items-center mb-4">
            <Percent className="h-8 w-8 mr-3" style={{ color: loanType.color }} />
            <h3 className="text-xl font-semibold text-[#2c3e50]">
              {i18n.t('loan_types_page.info.rate')}
            </h3>
          </div>
          <p className="text-lg text-gray-700 font-medium">{loanType.rate}</p>
        </div>

        <div className="bg-white p-8 rounded-lg shadow-lg border-l-4" style={{ borderLeftColor: loanType.color }}>
          <div className="flex items-center mb-4">
            <Shield className="h-8 w-8 mr-3" style={{ color: loanType.color }} />
            <h3 className="text-xl font-semibold text-[#2c3e50]">
              {i18n.t('loan_types_page.info.security')}
            </h3>
          </div>
          <p className="text-lg text-gray-700 font-medium">{loanType.security}</p>
        </div>
      </div>

      {/* Benefits */}
      <div className="mb-16">
        <h2 className="text-3xl font-bold text-[#2c3e50] mb-8 text-center">
          {i18n.t('loan_types_page.benefits.title')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loanType.benefits.map((benefit, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-start">
                <CheckCircle 
                  className="h-6 w-6 mr-3 mt-1 flex-shrink-0" 
                  style={{ color: loanType.color }}
                />
                <p className="text-gray-700 leading-relaxed">{benefit}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <CTASection variant="inline" color={loanType.color} />

      {/* How to Apply */}
      <div className="mb-16">
        <h2 className="text-3xl font-bold text-[#2c3e50] mb-8 text-center">
          {i18n.t('loan_types_page.how_to_apply.title')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center">
            <div 
              className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-white text-2xl font-bold"
              style={{ backgroundColor: loanType.color }}
            >
              1
            </div>
            <h3 className="text-xl font-semibold text-[#2c3e50] mb-3">
              {i18n.t('loan_types_page.how_to_apply.step1.title')}
            </h3>
            <p className="text-gray-600">
              {i18n.t('loan_types_page.how_to_apply.step1.description')}
            </p>
          </div>

          <div className="text-center">
            <div 
              className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-white text-2xl font-bold"
              style={{ backgroundColor: loanType.color }}
            >
              2
            </div>
            <h3 className="text-xl font-semibold text-[#2c3e50] mb-3">
              {i18n.t('loan_types_page.how_to_apply.step2.title')}
            </h3>
            <p className="text-gray-600">
              {i18n.t('loan_types_page.how_to_apply.step2.description')}
            </p>
          </div>

          <div className="text-center">
            <div 
              className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-white text-2xl font-bold"
              style={{ backgroundColor: loanType.color }}
            >
              3
            </div>
            <h3 className="text-xl font-semibold text-[#2c3e50] mb-3">
              {i18n.t('loan_types_page.how_to_apply.step3.title')}
            </h3>
            <p className="text-gray-600">
              {i18n.t('loan_types_page.how_to_apply.step3.description')}
            </p>
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="mb-16">
        <h2 className="text-3xl font-bold text-[#2c3e50] mb-8 text-center">
          {i18n.t('loan_types_page.details.title')}
        </h2>
        <div className="bg-white p-8 rounded-lg shadow-lg">
          <div className="space-y-6">
            {loanType.details.map((detail, index) => (
              <p key={index} className="text-gray-700 leading-relaxed text-lg">
                {detail}
              </p>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="mb-16">
        <h2 className="text-3xl font-bold text-[#2c3e50] mb-8 text-center">
          {i18n.t('loan_types_page.faq.title')}
        </h2>
        <div className="space-y-4">
          {loanType.faq.map((item, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden">
              <button
                onClick={() => toggleFaq(index)}
                className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
              >
                <h3 className="text-lg font-semibold text-[#2c3e50] pr-4">
                  {item.question}
                </h3>
                {expandedFaq === index ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                )}
              </button>
              {expandedFaq === index && (
                <div className="px-6 pb-4">
                  <p className="text-gray-700 leading-relaxed">{item.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LoanTypeContent; 